import { Link } from '@tanstack/react-router';
import { FacebookIcon, InstagramIcon, XSocialIcon } from './icons';
import { LogoIcon } from './logo-icon';
import { useLingui } from '@lingui/react/macro';
import { Image } from '@/routes/-components/image';

export function Footer() {
	const { t } = useLingui();
	const currentYear = new Date().getFullYear();

	return (
		<footer className="bg-footer text-primary-foreground flex flex-col gap-6 px-6 py-12">
			<div className="mx-auto flex w-full max-w-7xl flex-wrap justify-between gap-8 text-sm">
				<div className="flex w-full flex-col items-center gap-4 md:w-auto">
					<Link to="/www" className="flex flex-col justify-between">
						<span className="sr-only">Worldcoo Logo</span>
						<LogoIcon
							className="h-[67.36px] w-[120px]"
							style={
								{
									'--color-logo': 'var(--color-background)',
									'--color-primary': 'var(--primary)',
								} as React.CSSProperties
							}
						/>
					</Link>
					<a
						href="https://www.bcorporation.net/en-us/find-a-b-corp/company/worldcoo"
						target="_blank"
						rel="noopener noreferrer"
						className="bg-primary-foreground w-fit rounded-lg p-3 transition-opacity hover:opacity-80"
					>
						<span className="sr-only">B Corp Certified</span>
						<Image
							src="/bcorp-logo-no-border-gray.webp"
							className=""
							height={96}
							aspectRatio={96 / 161}
							alt={t`B Corp Certified`}
						/>
					</a>
				</div>

				<ul className="flex flex-col gap-1.5">
					<li>
						<Link to="/www/round-up" className="hover:text-primary aria-[current=page]:text-primary transition-colors">
							{t`Charity Rounding Up`}
						</Link>
					</li>
					<li>
						<a href="https://cert.worldcoo.com/" className="hover:text-primary transition-colors">
							{t`Donation Certificate`}
						</a>
					</li>
					<li>
						<Link to="/www/about-us" className="hover:text-primary aria-[current=page]:text-primary transition-colors">
							{t`About Us`}
						</Link>
					</li>
					<li>
						<Link
							to="/www/transparency"
							className="hover:text-primary aria-[current=page]:text-primary transition-colors"
						>
							{t`Transparency`}
						</Link>
					</li>
					<li>
						<Link to="/www/contact" className="hover:text-primary aria-[current=page]:text-primary transition-colors">
							{t`Contact`}
						</Link>
					</li>
				</ul>

				<ul className="flex flex-col gap-1.5">
					<li>
						<Link
							to="/www/legal-disclaimer"
							className="hover:text-primary aria-[current=page]:text-primary transition-colors"
						>
							{t`Legal Notice`}
						</Link>
					</li>
					<li>
						<Link
							to="/www/privacy-policy"
							className="hover:text-primary aria-[current=page]:text-primary transition-colors"
						>
							{t`Privacy Policy`}
						</Link>
					</li>
					<li>
						<Link to="/www/cookies" className="hover:text-primary aria-[current=page]:text-primary transition-colors">
							{t`Cookies`}
						</Link>
					</li>
					<li>
						<Link
							to="/www/selection-criteria"
							className="hover:text-primary aria-[current=page]:text-primary transition-colors"
						>
							{t`Selection Criteria`}
						</Link>
					</li>
				</ul>

				<ul className="flex max-w-60 flex-col gap-2">
					<li>
						<a
							href="https://goo.gl/maps/5DDj97UEuZTjRb9P9"
							target="_blank"
							rel="noopener noreferrer"
							className="hover:text-primary transition-colors"
						>
							Carrer de Llull, 108, Sant Martí, 08005 Barcelona
						</a>
					</li>
					<li>
						<a href="tel:+34936110104" className="hover:text-primary transition-colors">
							T. (+34) 936 11 01 04
						</a>
					</li>
					<li>
						<a href="mailto:contact@worldcoo.com" className="hover:text-primary transition-colors">
							contact@worldcoo.com
						</a>
					</li>
				</ul>

				<div className="flex flex-col gap-6">
					<p>{t`Join our community`}</p>

					<ul className="flex gap-6">
						<li>
							<a href="https://www.instagram.com/worldcoo/" target="_blank" rel="noopener noreferrer">
								<span className="sr-only">Instagram</span>
								<div className="bg-background hover:bg-primary size-7 rounded-full p-1.5 transition-colors">
									<InstagramIcon className="text-foreground" />
								</div>
							</a>
						</li>
						<li>
							<a href="https://twitter.com/worldcoo" target="_blank" rel="noopener noreferrer">
								<span className="sr-only">Twitter</span>
								<div className="bg-background hover:bg-primary size-7 rounded-full p-1.5 transition-colors">
									<XSocialIcon className="text-foreground" />
								</div>
							</a>
						</li>
						<li>
							<a href="https://www.facebook.com/worldcoo/" target="_blank" rel="noopener noreferrer">
								<span className="sr-only">Facebook</span>
								<div className="fill-background hover:fill-primary size-7 rounded-full transition-colors">
									<FacebookIcon />
								</div>
							</a>
						</li>
						<li>
							<a href="https://www.linkedin.com/company/worldcoo" target="_blank" rel="noopener noreferrer">
								<span className="sr-only">LinkedIn</span>
								<div className="bg-background text-footer hover:bg-primary size-7 rounded-full p-1.5 text-center text-lg leading-none font-bold transition-colors">
									in
								</div>
							</a>
						</li>
					</ul>
				</div>
			</div>

			<p className="mx-auto w-full max-w-7xl shrink-0 text-xs">
				Worldcoo &copy; {currentYear} | {t`Made with love in Barcelona`}
			</p>
		</footer>
	);
}
